import React, { useEffect } from 'react';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import { useTicketGroups } from '../TicketGroupsContext';
import Button from '@dashboard/components/Button';
import Reorderable from '@dashboard/components/Reorderable';
import TicketTypeEdit from './TicketTypeEdit';
import TicketGroupListItem from './TicketGroupListItem';
import useTicketTypeHelpers from '../useTicketTypeHelpers';
import clsx from 'clsx';
import { Label } from '@dashboard/components/Form';
import TicketTypesList from './TicketTypesList';
import TicketGroupLayoutForm from './TicketGroupLayoutForm';
import TicketGroupSettingsForm from './TicketGroupSettingsForm';
import { Cog8ToothIcon, EllipsisVerticalIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import Tooltip from '@dashboard/components/Tooltip';
import { confirm } from '@dashboard/lib/confirm';
import TicketGroupSaleDate from '@/dashboard/scenes/PageOverview/components/TicketGroupSaleDate';
import TicketGroupBadges from './TicketGroupBadges';
import usePage from '@/dashboard/hooks/usePage';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTicketGroupHelpers } from '../useTicketGroupHelpers';

function SingleTicketGroupView({
  ticketGroup,
  isAddingTicketType,
  isEditingTicketType,
  editingTicketTypeId,
  onAddTicketGroup,
  onAddTicketType,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  isEditingTicketGroupLayout,
  setIsEditingTicketGroupLayout,
  isEditingTicketGroupSettings,
  setIsEditingTicketGroupSettings,
  isNewPageFlow = false,
}) {
  const { onDuplicateTicketGroup, totalQuantitySoldInGroup } = useTicketGroupHelpers(
    ticketGroup._id,
  );

  const { page } = usePage();

  if (ticketGroup.ticketTypes.length === 0) {
    return (
      <div>
        {isAddingTicketType && (
          <div
            className={clsx('mt-4 pr-8', {
              'pl-6': ticketGroup.ticketTypes.length > 0,
            })}
          >
            <Label>New ticket</Label>

            <TicketTypeEdit ticketGroupId={ticketGroup._id} />
          </div>
        )}
      </div>
    );
  }

  const isEditingLastTicketType =
    isEditingTicketType &&
    editingTicketTypeId === ticketGroup.ticketTypes[ticketGroup.ticketTypes.length - 1]._id;

  const enabledTicketTypesCount = ticketGroup.ticketTypes?.filter(
    ticketTypes => ticketTypes.status === 'enabled',
  ).length;

  return (
    <>
      <div>
        <div
          className={clsx(
            `py-3 pl-9 gap-2 truncate w-full text-left flex items-center justify-between disabled:opacity-80 rounded focus:outline-none focus:ring-1 focus:ring-chocolate-200 transition-colors`,
            {
              'opacity-50 pointer-events-none': ticketGroup.status === 'archived',
            },
          )}
        >
          <div className="flex flex-col gap-y-0.5 flex-1 min-w-0">
            <span
              className={clsx('font-medium truncate overflow-hidden mr-2', {
                'opacity-50': ticketGroup.status === 'disabled',
              })}
            >
              {ticketGroup.name}
            </span>
            <TicketGroupBadges
              page={page}
              ticketGroup={ticketGroup}
              allTicketGroups={[ticketGroup]}
            />
            {ticketGroup.availabilityStatus === 'available' && ticketGroup.status === 'enabled' && (
              <TicketGroupSaleDate
                page={page}
                ticketGroup={ticketGroup}
                allTicketGroups={[ticketGroup]}
              />
            )}
            <div className="text-sm text-gray-700">
              {ticketGroup?.capacity !== null ? `${ticketGroup.capacity} tickets` : 'Unlimited'}
              {' • '}
              {totalQuantitySoldInGroup || '0'} sold
            </div>
          </div>
          <div className="flex gap-x-2 items-center flex-shrink-0">
            <>
              <span className="text-xs text-gray-500">
                {enabledTicketTypesCount} ticket
                {enabledTicketTypesCount === 1 ? '' : 's'}
              </span>
            </>
          </div>

          <div className="flex ml-auto gap-2 items-center">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button aria-label="more options" appearance="icon-button" size="narrow">
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="origin-top-right absolute right-0 z-20 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-gray-100 focus:outline-none overflow-hidden"
                  side="bottom"
                  align="end"
                >
                  <DropdownMenu.Item
                    onClick={onDuplicateTicketGroup}
                    className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                  >
                    Duplicate
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>

        <div className="flex flex-col gap-y-2">
          <TicketTypesList ticketTypes={ticketGroup.ticketTypes} ticketGroupId={ticketGroup._id} />
        </div>

        {isAddingTicketType && (
          <div
            className={clsx('mt-4 pr-8', {
              'pl-6': ticketGroup.ticketTypes.length > 0,
            })}
          >
            <Label>New ticket</Label>
            <TicketTypeEdit ticketGroupId={ticketGroup._id} />
          </div>
        )}

        {!isAddingTicketType && !isEditingTicketType && (
          <>
            <div
              className={clsx('mt-4 flex gap-x-2 ml-6 mr-[26px] justify-between', {
                'border-t pt-8': isEditingLastTicketType,
              })}
            >
              <div className="flex gap-x-2">
                <Tooltip label="Group settings">
                  <Button
                    handleClick={async () => {
                      let proceed = true;

                      if (hasUnsavedChanges) {
                        proceed = await confirm('Discard unsaved changes?', {
                          confirm: 'Discard',
                        });
                      }

                      if (!proceed) return;

                      setIsEditingTicketGroupSettings(!isEditingTicketGroupSettings);
                      setIsEditingTicketGroupLayout(false);
                      setHasUnsavedChanges(false);
                    }}
                    appearance="secondary"
                    className={clsx({
                      '!border-chocolate-1000': isEditingTicketGroupSettings,
                    })}
                  >
                    <Cog8ToothIcon className="w-5 h-5" />
                  </Button>
                </Tooltip>

                {!isNewPageFlow && (
                  <Tooltip label="Group layout">
                    <Button
                      handleClick={async () => {
                        let proceed = true;

                        if (hasUnsavedChanges) {
                          proceed = await confirm('Discard unsaved changes?', {
                            confirm: 'Discard',
                          });
                        }

                        if (!proceed) return;

                        setIsEditingTicketGroupLayout(!isEditingTicketGroupLayout);
                        setIsEditingTicketGroupSettings(false);
                        setHasUnsavedChanges(false);
                      }}
                      appearance="secondary"
                      className={clsx({
                        '!border-chocolate-1000': isEditingTicketGroupLayout,
                      })}
                    >
                      <Squares2X2Icon className="w-5 h-5" />
                    </Button>
                  </Tooltip>
                )}

                <Button handleClick={onAddTicketGroup} appearance="secondary">
                  + Add group
                </Button>
              </div>

              <Button
                appearance={isNewPageFlow ? 'secondary' : 'primary'}
                onClick={() => onAddTicketType(ticketGroup._id)}
              >
                + Add ticket
              </Button>
            </div>

            {(isEditingTicketGroupLayout || isEditingTicketGroupSettings) && (
              <div className="ml-6 mr-[26px] mt-4">
                {isEditingTicketGroupLayout && (
                  <TicketGroupLayoutForm
                    ticketGroup={ticketGroup}
                    onClose={() => {
                      setIsEditingTicketGroupLayout(false);
                      setHasUnsavedChanges(false);
                    }}
                  />
                )}

                {isEditingTicketGroupSettings && (
                  <TicketGroupSettingsForm
                    ticketGroup={ticketGroup}
                    onClose={() => {
                      setIsEditingTicketGroupSettings(false);
                      setHasUnsavedChanges(false);
                    }}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default function TicketGroupsList({ isNewPageFlow = false }) {
  const { state: ticketGroups } = useTicketGroups();
  const {
    onReorderTicketGroups,
    isAddingTicketType,
    isEditingTicketType,
    onAddTicketGroup,
    editingTicketTypeId,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isEditingTicketGroupLayout,
    setIsEditingTicketGroupLayout,
    isEditingTicketGroupSettings,
    setIsEditingTicketGroupSettings,
  } = useTicketGroupEdit();

  const { onAddTicketType } = useTicketTypeHelpers();

  const enabledTicketGroupsCount = ticketGroups.filter(
    ticketGroup => ticketGroup.status === 'enabled',
  ).length;

  // Automatically open forms when no tickets exist
  useEffect(() => {
    // If no ticket groups exist, automatically open the ticket group creation form
    if (ticketGroups.length === 0) {
      onAddTicketGroup();
      return;
    }

    // If there's exactly one ticket group but no ticket types, automatically open ticket type creation
    if (enabledTicketGroupsCount === 1 && ticketGroups.length === 1) {
      const ticketGroup = ticketGroups[0];
      if (ticketGroup.ticketTypes.length === 0 && !isAddingTicketType && !isEditingTicketType) {
        onAddTicketType();
      }
    }
  }, [
    ticketGroups.length,
    enabledTicketGroupsCount,
    ticketGroups,
    onAddTicketGroup,
    onAddTicketType,
    isAddingTicketType,
    isEditingTicketType,
  ]);

  if (ticketGroups.length === 0) {
    // The useEffect will automatically trigger onAddTicketGroup, so we show nothing
    return null;
  }

  if (enabledTicketGroupsCount === 1 && ticketGroups.length === 1) {
    const ticketGroup = ticketGroups[0];

    return (
      <SingleTicketGroupView
        ticketGroup={ticketGroup}
        isAddingTicketType={isAddingTicketType}
        isEditingTicketType={isEditingTicketType}
        editingTicketTypeId={editingTicketTypeId}
        onAddTicketGroup={onAddTicketGroup}
        onAddTicketType={onAddTicketType}
        hasUnsavedChanges={hasUnsavedChanges}
        setHasUnsavedChanges={setHasUnsavedChanges}
        isEditingTicketGroupLayout={isEditingTicketGroupLayout}
        setIsEditingTicketGroupLayout={setIsEditingTicketGroupLayout}
        isEditingTicketGroupSettings={isEditingTicketGroupSettings}
        setIsEditingTicketGroupSettings={setIsEditingTicketGroupSettings}
        isNewPageFlow={isNewPageFlow}
      />
    );
  }

  return (
    <div>
      <div className="mb-4">
        <Reorderable.Wrapper>
          <Reorderable
            id="ticketGroups"
            items={ticketGroups}
            onReorder={onReorderTicketGroups}
            renderItem={({ item: ticketGroup, dragHandleProps, isDragging }) => (
              <Reorderable.Item isDragging={isDragging}>
                <TicketGroupListItem
                  ticketGroup={ticketGroup}
                  dragHandleProps={dragHandleProps}
                  isNewPageFlow={isNewPageFlow}
                />
              </Reorderable.Item>
            )}
          />
        </Reorderable.Wrapper>
      </div>

      {!isAddingTicketType && !isEditingTicketGroupLayout && !isEditingTicketGroupSettings && (
        <div className="flex gap-x-2">
          {/*           <div className="mb-8">
            <Collapsible.Root
              open={ticketGroupsSettingsOpen}
              onOpenChange={setTicketGroupsSettingsOpen}
            >
              <Collapsible.Trigger className="flex items-center gap-x-1 text-gray-900 hover:text-gray-500 font-medium text-sm">
                <div className="flex-1 text-left">Settings & layout</div>

                {ticketGroupsSettingsOpen ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </Collapsible.Trigger>

              <Collapsible.Content className="CollapsibleContentAnimation mt-2">
                <TicketGroupsSettings />
              </Collapsible.Content>
            </Collapsible.Root>
          </div> */}

          <Button handleClick={onAddTicketGroup} appearance="secondary">
            + Add group
          </Button>
        </div>
      )}
    </div>
  );
}
